// @flow
import React from 'react';
import {Query} from '@apollo/react-components';
import type {QueryOpts, OperationComponent} from '@apollo/react-components';

type OperationOptions<TVariables> = {
    +document: Object,
    +options?: QueryOpts<TVariables>,
    +skip?: boolean
};

/**
 * Lite copy of react-apollo 'graphql' HOC to be able to map props to document
 * HOCs must be replaced with with 'useLazyQuery' hook when on react >= 16.9 & react-apollo >= 3.x
 */
const withQuery = <TResult, TProps, TVariables, TChildProps>(
    operationOptions: (props: TProps) => OperationOptions<TVariables>
): OperationComponent<TResult, TProps, TVariables, TChildProps> => (
    WrappedComponent
) => {
    const WithQueryComponent = (props: TProps) => {
        const {document, skip, options} = operationOptions(props);

        return (
            <Query
                {...(skip ? null : options)}
                skip={skip}
                query={document}
                warnUnhandledError
            >
                {({client: _, data, ...rest}) => {
                    if (skip) return <WrappedComponent {...props} />;

                    return (
                        <WrappedComponent
                            {...props}
                            data={{...rest, ...data}}
                        />
                    );
                }}
            </Query>
        );
    };

    WithQueryComponent.displayName = `Apollo(${WrappedComponent.displayName ||
        WrappedComponent.name ||
        'Component'})`;

    return WithQueryComponent;
};

export default withQuery;
